// Canvas utilities for passport photo processing.
//
// Provides functions for compositing, cropping, and tiling images.
//

import {
  calculatePrintLayout,
  PHOTO_SIZES,
  type PhotoSize,
  PRINT_SIZES,
  type PrintSize,
} from "./dimensions";

function getCanvas2DContext(
  canvas: HTMLCanvasElement,
): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  return ctx;
}

// Loads an image from a Blob or URL.
//
export async function loadImage(
  source: Blob | string,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;

    if (source instanceof Blob) {
      img.src = URL.createObjectURL(source);
    } else {
      img.src = source;
    }
  });
}

// Adds a white background to a transparent image.
//
export async function addWhiteBackground(transparentBlob: Blob): Promise<Blob> {
  const img = await loadImage(transparentBlob);

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = getCanvas2DContext(canvas);

  // Fill with white background.
  //
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw the transparent image on top.
  //
  ctx.drawImage(img, 0, 0);

  // Clean up object URL if we created one.
  //
  URL.revokeObjectURL(img.src);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      },
      "image/png",
      1.0,
    );
  });
}

// Crop position and zoom configuration.
//
export interface CropConfig {
  x: number; // Offset X (0 = centered)
  y: number; // Offset Y (0 = centered)
  zoom: number; // Zoom level (1 = fit)
  previewSize?: number; // Preview frame size used when this config was made
}

// Fallback preview container size for legacy crop configs.
//
export const DEFAULT_PREVIEW_CONTAINER_SIZE = 320;

// Crops an image to the selected passport photo size.
//
// The crop is applied based on the provided position and zoom, then scaled to
// the selected photo dimensions at 300 DPI.
//
// The cropConfig values (x, y, zoom) are relative to the preview container
// width. We need to scale them appropriately for the output width.
//
export async function cropToPhotoSize(
  sourceBlob: Blob,
  cropConfig: CropConfig,
  photoSize: PhotoSize,
  previewSize: number = cropConfig.previewSize ??
    DEFAULT_PREVIEW_CONTAINER_SIZE,
): Promise<Blob> {
  const img = await loadImage(sourceBlob);
  const photo = PHOTO_SIZES[photoSize];

  const canvas = document.createElement("canvas");
  canvas.width = photo.pixels.width;
  canvas.height = photo.pixels.height;

  const ctx = getCanvas2DContext(canvas);

  // Fill with white background.
  //
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const safePreviewSize =
    previewSize > 0 ? previewSize : DEFAULT_PREVIEW_CONTAINER_SIZE;

  // Scale factor from the preview width to the output width.
  //
  const scaleFactor = photo.pixels.width / safePreviewSize;

  // In the preview, the image is:
  // 1. Displayed at natural size
  // 2. Centered in container
  // 3. Translated by (x, y)
  // 4. Scaled by zoom around its center
  //
  // The visible region is the preview frame's window into the transformed
  // image. The preview and output share the selected photo's aspect ratio.
  //
  // Draw dimensions: natural size * zoom, scaled up for output resolution.
  //
  const drawWidth = img.width * cropConfig.zoom * scaleFactor;
  const drawHeight = img.height * cropConfig.zoom * scaleFactor;

  // Position: center in canvas, then apply scaled offset.
  //
  const drawX = (canvas.width - drawWidth) / 2 + cropConfig.x * scaleFactor;
  const drawY = (canvas.height - drawHeight) / 2 + cropConfig.y * scaleFactor;

  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

  URL.revokeObjectURL(img.src);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      },
      "image/png",
      1.0,
    );
  });
}

// Tiles passport photos onto a print-sized canvas.
//
// If multiple photos are provided, they fill the grid in order.
// If fewer photos than grid slots, remaining slots are left white.
// If more photos than grid slots, extra photos are ignored.
//
export async function tileForPrint(
  photoBlobs: Blob[],
  printSize: PrintSize,
  photoSize: PhotoSize,
  photoGap: number,
): Promise<Blob> {
  // Load all images.
  //
  const images = await Promise.all(photoBlobs.map((blob) => loadImage(blob)));
  const print = PRINT_SIZES[printSize];
  const photo = PHOTO_SIZES[photoSize];
  const layout = calculatePrintLayout(printSize, photoSize, photoGap);

  const canvas = document.createElement("canvas");
  canvas.width = print.pixels.width;
  canvas.height = print.pixels.height;

  const ctx = getCanvas2DContext(canvas);

  // Fill the full print area gray. Photos are drawn over it below, leaving all
  // internal gaps and outer margins visible as cutting space.
  //
  ctx.fillStyle = "#666666";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Center the grid on the canvas.
  //
  const { grid } = layout;
  const startX = layout.offset.x;
  const startY = layout.offset.y;

  // Tile the passport photos.
  // Distribute images evenly across all slots, grouping copies together.
  //
  const totalSlots = grid.rows * grid.columns;

  let slotIndex = 0;

  for (let row = 0; row < grid.rows; row++) {
    for (let col = 0; col < grid.columns; col++) {
      // Distribute images evenly: maps slot index to image index proportionally.
      // This ensures all images are used even when slots don't divide evenly.
      //
      const imageIndex = Math.floor((slotIndex * images.length) / totalSlots);
      const img = images[imageIndex];
      if (!img) continue;

      const x = startX + col * (photo.pixels.width + photoGap);
      const y = startY + row * (photo.pixels.height + photoGap);

      ctx.drawImage(img, x, y, photo.pixels.width, photo.pixels.height);

      slotIndex++;
    }
  }

  // Clean up object URLs.
  //
  images.forEach((img) => URL.revokeObjectURL(img.src));

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      },
      "image/png",
      1.0,
    );
  });
}

// Downloads a blob as a file.
//
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Creates a preview canvas for displaying in the UI.
//
// Returns a data URL for use in img src.
//
export async function createPreviewDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
